import { ArtifactTypeEnum, Attachment, ConversationTypeEnum } from '@/typings/domain'
import { createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findLast, isNil, last } from 'lodash-es'
import type { RootState } from '../store'
import { QueryState } from './queryStateType'

export type TextPart = {
  key?: string
  type: 'text'
  content: string
}

export type ThinkingPart = {
  key?: string
  type: 'thinking'
  content: string
}

export type ErrorPart = {
  key: string
  type: 'error'
  message: string
}

export type AttachmentPart = {
  key: string
  type: 'attachment'
  attachment: {
    id: string
    visitUrl: string
    fileName: string
    fileSize: number
    contentType: string
    fileWidth?: Maybe<number>
    fileHeight?: Maybe<number>
  }
}

export type ArtifactPart = {
  key: string
  type: 'artifact'
  artifactType: ArtifactTypeEnum
  artifactId: string
}

export type MessagePart = TextPart | ThinkingPart | ErrorPart | AttachmentPart | ArtifactPart

type ConversationMessageMarks = {
  backendMessageId?: string
  isCheckRiskRejectedMessage?: boolean
}

export type ConversationMessage = {
  id: string
  role: 'user' | 'assistant'
  parts: MessagePart[]
  done: boolean
  interrupted?: boolean

  stepDetails: { step: string; done: boolean; result?: unknown }[]
  debugInfos?: string[]
} & ConversationMessageMarks

export type Conversation = {
  id: string
  backendConversationId?: string
  type?: Maybe<ConversationTypeEnum>
  subject?: Maybe<string>

  userInput: {
    content: string
    attachments: Attachment[]
  }
  messages: ConversationMessage[]

  activeArtifactId?: string

  interrupted?: true
} & QueryState

export type UpsertMessagePayload = {
  id: string
  role?: ConversationMessage['role']
  chunks?: MessagePart[]
  done?: boolean
  interrupted?: boolean
  stepUpdate?: { step: string; done?: boolean; result?: unknown }
  debugInfos?: string[]
} & ConversationMessageMarks

const conversationAdapter = createEntityAdapter<Conversation>()

type AIChatState = {
  currentConversationId?: string
}

const formAIGenerationSlice = createSlice({
  name: 'aiChat',
  initialState: conversationAdapter.getInitialState<AIChatState>({}),
  reducers: {
    createNewConversation: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      if (state.entities[id]) throw new Error(`Conversation ${id} already exists`)

      conversationAdapter.addOne(state, {
        id,
        userInput: { content: '', attachments: [] },
        messages: [],
        isLoading: false,
        loaded: false,
      })
    },
    setBackendConversationId: (
      state,
      action: PayloadAction<{ conversationId: string; backendConversationId: string | undefined }>
    ) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.backendConversationId = action.payload.backendConversationId
    },
    removeConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      conversationAdapter.removeOne(state, action.payload.conversationId)
    },
    setActiveArtifactId: (state, action: PayloadAction<{ conversationId: string; artifactId: string | undefined }>) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.activeArtifactId = action.payload.artifactId
    },
    setUserInputContent: (state, action: PayloadAction<{ conversationId: string; content: string }>) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.userInput.content = action.payload.content
    },
    setUserInputAttachments: (
      state,
      { payload }: PayloadAction<{ conversationId: string; attachments: Attachment[] }>
    ) => {
      const conversation = mustSelectConversation(state, payload.conversationId)
      conversation.userInput.attachments = payload.attachments
    },
    removeAllUserInputAttachments: (state, action: PayloadAction<{ conversationId: string }>) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.userInput.attachments = []
    },
    upsertMessage: (state, { payload }: PayloadAction<{ conversationId: string; message: UpsertMessagePayload }>) => {
      const conversation = mustSelectConversation(state, payload.conversationId)
      let message = findLast(conversation.messages, message => message.id === payload.message.id)
      if (!message) {
        if (!payload.message.role) throw new Error('Message role is required')
        message = {
          id: payload.message.id,
          role: payload.message.role,
          parts: [],
          done: false,
          stepDetails: [],
        }
        conversation.messages.push(message)
      }

      if (!isNil(payload.message.done)) message.done = payload.message.done
      if (!isNil(payload.message.interrupted)) message.interrupted = payload.message.interrupted

      if (payload.message.debugInfos?.length) {
        message.debugInfos = [...(message.debugInfos ?? []), ...payload.message.debugInfos]
      }

      const { chunks, backendMessageId, isCheckRiskRejectedMessage, stepUpdate } = payload.message

      if (chunks) {
        for (const chunk of chunks) {
          let existingPart = message.parts.find(item => item.type === chunk.type && item.key === chunk.key)
          if (!existingPart) {
            existingPart = { ...chunk }
            message.parts.push(existingPart)
          } else {
            if (chunk.type === 'text' && existingPart.type === 'text') {
              existingPart.content += chunk.content
            } else if (chunk.type === 'thinking' && existingPart.type === 'thinking') {
              existingPart.content += chunk.content
            } else {
              Object.assign(existingPart, chunk)
            }
          }
        }
      }

      if (stepUpdate) {
        const { stepDetails } = message
        let stepDetail = stepDetails.find(stepDetail => stepDetail.step === stepUpdate.step)
        if (!stepDetail) {
          stepDetail = { step: stepUpdate.step, done: false, result: undefined }
          stepDetails.push(stepDetail)
        }
        if (stepUpdate.done !== undefined) stepDetail.done = stepUpdate.done
        if (stepUpdate.result !== undefined) stepDetail.result = stepUpdate.result
      }

      if (!isNil(backendMessageId)) message.backendMessageId = backendMessageId
      if (!isNil(isCheckRiskRejectedMessage)) message.isCheckRiskRejectedMessage = isCheckRiskRejectedMessage
    },
    setInterrupted: (state, action: PayloadAction<{ conversationId: string }>) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.interrupted = true
    },
    resetInterruptedState: (state, action: PayloadAction<{ conversationId: string }>) => {
      const conversation = mustSelectConversation(state, action.payload.conversationId)
      conversation.interrupted = undefined
    },
  },
})

function mustSelectConversation(state: RootState['aiChat'], conversationId: string) {
  const conversation = selectConversation(state, conversationId)
  if (!conversation) throw new Error(`Conversation ${conversationId} not found`)
  return conversation
}

export function selectConversation(state: RootState['aiChat'], conversationId: string) {
  return state.entities[conversationId]
}

export const selectConversationStarted = (state: RootState, conversationId: string) => {
  const conversation = state.aiChat.entities[conversationId]
  return !!conversation?.messages.length
}

export const selectConversationMessageById = (state: RootState, conversationId: string, messageId: string) => {
  const conversation = selectConversation(state.aiChat, conversationId)
  return conversation?.messages.find(message => message.id === messageId)
}

export const selectLastUserMessage = (state: RootState, conversationId: string) => {
  const conversation = selectConversation(state.aiChat, conversationId)
  const lastMessage = last(conversation?.messages)
  if (lastMessage?.role === 'user') return lastMessage
  return null
}

export const selectIsLastMessageGenerating = (state: RootState, conversationId: string) => {
  const conversation = selectConversation(state.aiChat, conversationId)
  const lastMessage = last(conversation?.messages)
  return lastMessage?.done === false
}

export const selectArtifactPartById = createSelector(
  (state: RootState['aiChat'], conversationId: string) => selectConversation(state, conversationId)?.messages,
  (_state: RootState['aiChat'], _conversationId: string, artifactId: string) => artifactId,
  (messages, artifactId) => {
    if (!messages) return undefined
    for (const message of messages) {
      for (const part of message.parts) {
        if (part.type === 'artifact' && part.artifactId === artifactId) return part
      }
    }
  }
)

export const {
  createNewConversation,
  setBackendConversationId,
  removeConversation,
  setActiveArtifactId,
  setUserInputContent,
  setUserInputAttachments,
  removeAllUserInputAttachments,
  upsertMessage,
  setInterrupted,
  resetInterruptedState,
} = formAIGenerationSlice.actions

export default formAIGenerationSlice.reducer
